/**
 * Por qué NO se pudo crear una solicitud de documentos, dicho con la verdad.
 *
 * ── EL FALLO QUE ARREGLA ────────────────────────────────────────────────────
 *
 * Lissette, 2026-09-01: *"está dando error cuando se le da solicitar documentos
 * desde la parte de la elegibilidad del cliente"*.
 *
 * El servidor rechaza esa llamada por CUATRO motivos distintos, y cada uno con
 * su código:
 *
 *     PHI_DETECTED      posible dato sensible en el texto — se puede confirmar
 *     no_supervisor     el asiento no tiene agente asignado
 *     agent_no_mailbox  el agente no ha conectado su correo
 *     Client not found  el cliente no está en el alcance de quien pulsa
 *
 * Pero `createDocRequest` en `api.js` hacía `throw new Error(err.error)`, que
 * TIRA el código y deja solo el texto crudo. Y la pantalla lo pegaba tal cual:
 *
 *     "No se pudo crear la solicitud: agent_no_mailbox"
 *
 * Eso no es un mensaje, es un identificador interno. Y el de PHI era peor: el
 * servidor lo manda como una PREGUNTA —"revísalo y reenvía confirmando"— y la
 * pantalla lo enseñaba como un error definitivo, así que no había forma de
 * seguir adelante. Correos Masivos y Firmas sí sabían contestar esa pregunta;
 * esta pantalla, no.
 *
 * Un solo sitio para el texto: lo usan la pestaña de Solicitudes y el panel de
 * Elegibilidad. Dos copias acabarían diciendo cosas distintas del mismo fallo
 * (E-138). Mismo criterio que `motivoDeCorreo.js`.
 */

/**
 * @param {Error & {code?: string, detail?: string}} err  Lo que lanzó la API.
 * @param {'es'|'en'} lang
 * @returns {{ texto: string, sePuedeConfirmar: boolean, irAlCorreo: boolean }}
 *   `sePuedeConfirmar` = el servidor está preguntando, no negándose: se puede
 *   reintentar con `acknowledgePhi`.
 *   `irAlCorreo` = se arregla conectando un buzón, así que la pantalla debería
 *   ofrecer el atajo en vez de dejar a la agente buscándolo.
 */
export function mensajeDeFalloDeSolicitud(err, lang) {
  const es = lang === 'es';
  // El código puede llegar en `code` (PHI) o en el propio mensaje (los demás,
  // que el servidor manda en el campo `error`).
  const codigo = String(err?.code || err?.message || '');

  if (codigo.includes('PHI_DETECTED')) {
    return {
      texto: es
        ? 'Detectamos posible información sensible en el texto de la solicitud (por ejemplo un número de Seguro Social o una fecha de nacimiento). ¿Enviarla de todos modos?'
        : 'We found possible sensitive info in the request text (for example a Social Security number or a date of birth). Send it anyway?',
      sePuedeConfirmar: true,
      irAlCorreo: false,
    };
  }

  if (codigo.includes('agent_no_mailbox')) {
    return {
      texto: es
        ? 'La solicitud sale del correo del agente, y todavía no hay ninguno conectado. Conéctalo en Mi Correo y vuelve a intentarlo.'
        : "The request goes out from the agent's email, and none is connected yet. Connect it under My Email and try again.",
      sePuedeConfirmar: false,
      irAlCorreo: true,
    };
  }

  if (codigo.includes('no_supervisor')) {
    return {
      texto: es
        ? 'Este asiento no tiene un agente asignado, así que la solicitud no puede salir a nombre de nadie. Asígnale un agente en Equipo.'
        : 'This seat has no agent assigned, so the request has no one to go out as. Assign an agent under Team.',
      sePuedeConfirmar: false,
      irAlCorreo: false,
    };
  }

  if (codigo.includes('Client not found')) {
    return {
      texto: es
        ? 'No se encontró este cliente entre los que puedes ver. Si es de otro agente, pídele a él que mande la solicitud.'
        : "This client isn't among the ones you can see. If they belong to another agent, ask them to send the request.",
      sePuedeConfirmar: false,
      irAlCorreo: false,
    };
  }

  // Cualquier otra cosa: se enseña el texto del servidor, pero anunciado como
  // lo que es. Inventar una explicación aquí sería repetir el fallo del aviso
  // que culpaba a un límite de ACS que no existía.
  return {
    texto: (es ? 'No se pudo crear la solicitud: ' : 'Could not create the request: ')
      + (err?.message || (es ? 'error desconocido' : 'unknown error')),
    sePuedeConfirmar: false,
    irAlCorreo: false,
  };
}
